/**
 * @cafe-os/db — super-admin v2 (migration 00101 + Edge Function).
 * RPCs gated by is_super_admin(). Все возвращают throw на P0068 если не super.
 */
import { callRpcWithDefaults } from "./errors";
import { getSupabaseClient } from "./client";

export interface DashboardStats {
  total_cafes: number;
  active_cafes: number;
  trial_cafes: number;
  suspended_cafes: number;
  mrr_kzt: number;
  /** actual currency of mrr_kzt (USD today; 'MIXED' if plans span currencies) — audit 2026-07-04 #4 */
  currency?: string;
  today_orders: number;
  today_revenue_kzt: number;
  weekly_revenue: Array<{ day: string; date: string; value: number }>;
}

export interface SubscriptionOverviewRow {
  tenant_id: string;
  plan_id: string;
  status: "trialing" | "active" | "past_due" | "blocked" | "cancelled";
  current_period_end: string;
  trial_ends_at: string | null;
  monthly_price: number;
  currency: string;
  kaspi_phone: string | null;
  payment_mode: string;
}

export interface TenantFullSnapshot {
  tenant: {
    id: string;
    name: Record<string, string> | null;
    phone: string | null;
    is_active: boolean;
    suspended_at: string | null;
    suspended_reason: string | null;
    created_at: string;
    settings: Record<string, unknown>;
  };
  subscription: SubscriptionOverviewRow | null;
  plan: {
    id: string;
    name: Record<string, string>;
    monthly_price: number;
    currency: string;
    features: Record<string, unknown>;
  } | null;
  invoices_summary: {
    total: number;
    paid: number;
    pending: number;
    last_paid_at: string | null;
  };
  stats: {
    today_revenue: number;
    today_orders: number;
    staff_count: number;
    last_order_at: string | null;
  };
  /** Владелец тенанта (00189). null если у кафе нет staff с ролью owner. */
  owner: {
    id: string;
    display_name: string | null;
    phone: string | null;
  } | null;
}

export async function superAdminDashboardStats(): Promise<DashboardStats> {
  const data = await callRpcWithDefaults<DashboardStats>("super_admin_dashboard_stats", {});
  if (!data) throw new Error("[db/superAdminDashboardStats] empty");
  return data;
}

export async function superAdminSubscriptionsOverview(): Promise<SubscriptionOverviewRow[]> {
  const data = await callRpcWithDefaults<SubscriptionOverviewRow[]>(
    "super_admin_subscriptions_overview", {});
  return Array.isArray(data) ? data : [];
}

export async function superAdminGetTenantFull(tenantId: string): Promise<TenantFullSnapshot> {
  const data = await callRpcWithDefaults<TenantFullSnapshot>(
    "super_admin_get_tenant_full", { p_tenant_id: tenantId });
  if (!data) throw new Error("[db/superAdminGetTenantFull] empty");
  return data;
}

export async function superAdminSuspendTenant(tenantId: string, reason: string): Promise<{ closed_sessions: number }> {
  const data = await callRpcWithDefaults<{ ok: boolean; closed_sessions: number }>(
    "super_admin_suspend_tenant",
    { p_tenant_id: tenantId, p_reason: reason });
  return { closed_sessions: data?.closed_sessions ?? 0 };
}

export async function superAdminUnsuspendTenant(tenantId: string): Promise<void> {
  await callRpcWithDefaults("super_admin_unsuspend_tenant", { p_tenant_id: tenantId });
}

/**
 * Mint a single-use, short-lived (3 min) server token authorizing deletion of one
 * tenant (audit 2026-07-04 #5). Replaces the old derivable 'DELETE-<slug>' phrase.
 * super-admin only (RPC raises P0068 otherwise).
 */
export async function superAdminRequestDeleteToken(
  tenantId: string,
): Promise<{ token: string; expires_at: string }> {
  const data = await callRpcWithDefaults<{ token: string; expires_at: string }>(
    "super_admin_request_delete_token",
    { p_tenant_id: tenantId },
  );
  if (!data?.token) throw new Error("[db/superAdminRequestDeleteToken] no token");
  return data;
}

/**
 * Delete a tenant using a server-issued token from superAdminRequestDeleteToken.
 * The token is validated and consumed server-side; a leaked/guessed string will not work.
 */
export async function superAdminDeleteTenant(tenantId: string, token: string): Promise<void> {
  await callRpcWithDefaults("super_admin_delete_tenant",
    { p_tenant_id: tenantId, p_token: token });
}

export type SubscriptionStatusValue =
  | "trialing" | "active" | "past_due" | "blocked" | "cancelled";

export interface ChangeTenantPlanResult {
  ok: boolean;
  changed: boolean;
  old_plan_id: string;
  new_plan_id: string;
  old_status: SubscriptionStatusValue;
  new_status: SubscriptionStatusValue;
  status_changed: boolean;
  period_reset: boolean;
  period_months: number | null;
}

/**
 * Меняет тариф / статус / срок у тенанта (migration 00120).
 * - `resetPeriod=true` → current_period_start=now(), end=now()+periodMonths (или cycle_months из plan если periodMonths null)
 * - `newStatus` null = не менять. Допустимые: trialing/active/past_due/blocked/cancelled
 * - `periodMonths` null = взять cycle_months из плана. Допустимый диапазон 1..60
 * - Если status→'active': очищаются trial_ends_at/cancelled_at/cancel_reason
 * - Бросит P0001 если план неактивен / тенант без subscription / invalid status
 */
export async function superAdminChangeTenantPlan(input: {
  tenantId: string;
  newPlanId: string;
  resetPeriod?: boolean;
  reason?: string;
  newStatus?: SubscriptionStatusValue | null;
  periodMonths?: number | null;
}): Promise<ChangeTenantPlanResult> {
  const data = await callRpcWithDefaults<ChangeTenantPlanResult>(
    "super_admin_change_tenant_plan",
    {
      p_tenant_id: input.tenantId,
      p_new_plan_id: input.newPlanId,
      p_reset_period: input.resetPeriod ?? false,
      p_reason: input.reason ?? null,
      p_new_status: input.newStatus ?? null,
      p_period_months: input.periodMonths ?? null,
    },
  );
  if (!data) throw new Error("[superAdminChangeTenantPlan] empty response");
  return data;
}

/**
 * Назначает подписку кафе БЕЗ подписки (SA-2, ensure_subscription_for_tenant
 * 00166): идемпотентна — если подписка уже есть, вернёт её без изменений.
 * status='trialing', trial_ends_at = now()+trialDays.
 */
export async function superAdminEnsureSubscription(
  tenantId: string,
  planId: string,
  trialDays: number,
): Promise<{ plan_id: string; status: string }> {
  const data = await callRpcWithDefaults<{ plan_id: string; status: string }>(
    "ensure_subscription_for_tenant",
    {
      p_tenant_id: tenantId,
      p_plan_id: planId,
      p_trial_days: trialDays,
    },
  );
  if (!data) throw new Error("[superAdminEnsureSubscription] empty response");
  return data;
}

export type ResetMethod = "email_link" | "new_password" | "pin";

export interface ResetCredentialsInput {
  tenantId: string;
  staffId: string;
  method: ResetMethod;
  newPassword?: string;
  newPin?: string;
}

export interface ResetCredentialsResult {
  ok: boolean;
  method: ResetMethod;
  email_sent?: boolean;
  password_set?: boolean;
  pin_set?: boolean;
  action_link?: string | null;
}

export async function superAdminResetOwnerCredentials(
  input: ResetCredentialsInput,
): Promise<ResetCredentialsResult> {
  const sb = getSupabaseClient();
  const { data, error } = await sb.functions.invoke<ResetCredentialsResult>(
    "superAdminResetOwnerCredentials",
    {
      body: {
        tenant_id: input.tenantId,
        staff_id: input.staffId,
        method: input.method,
        new_password: input.newPassword,
        new_pin: input.newPin,
      },
    },
  );
  if (error) throw error;
  if (!data) throw new Error("superAdminResetOwnerCredentials returned no data");
  return data;
}
